import { useEffect, useState } from "react";
import { DeploymentPage } from "./deployment/DeploymentPage";
import { Box, Button, CssBaseline, Link, Typography } from "@mui/material";
import PageHeader from "../components/PageHeader";
import { callRobotApi, WifiConfig } from "./hmi/robot-api";
import { OpenInBrowser, RestartAlt, WifiLock } from "@mui/icons-material";
import { PropValueLine } from "../panels/common/PropValueLine";
import { WiFiConfig } from "./hmi/WiFiConfig";
import { listPublicBlobs } from "../utils/transferToBlob";

interface HMIApkVersion {
  version: string;
  url: string;
}
interface ManualVersion {
  title: string;
  url: string;
}

export function HMISupportPage() {
  const [versions, setVersions] = useState<HMIApkVersion[]>([]);
  const [manuals, setManuals] = useState<ManualVersion[]>([]);
  const [targetVersion, setTargetVersion] = useState<string>('');
  useEffect(() => {
    document.title = "HMI Support - Techmage Robotics";
    const loader = async () => {
      const apkVersions: HMIApkVersion[] = await getApkVersions();
      setVersions(apkVersions);
      const manualBlobs = await listPublicBlobs('manuals');
      const manualVersions: ManualVersion[] = manualBlobs.blobs.map(blob => {
        return { title: blob.name.split('/')[1].replace(/_/g, ' ').replace('.pdf', ''), url: blob.url };
      });
      setManuals(manualVersions);
      if (window.location.hash) {
        const version = window.location.hash.substring(1);
        const element: any = document.getElementById(version);
        if (element) {
          console.log(`Starting download for version ${version}`);
          setTargetVersion(version);
          setTimeout(() => {
            window.location = element.href;
          }, 2500);
        } else {
          console.log(`Version ${version} not found`);
        }
        // console.log({ version });
      }
    };
    loader();
  }, []);
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          m: 0,
          backgroundColor: "#212121",
          color: "#ECECEC",
          minHeight: "100vh",
        }}
      >
        <PageHeader
          title={"HMI Support"}
          rightComponent={[]}
        />
        {targetVersion && (
          <Box sx={{ p: 2, backgroundColor: '#323232', color: '#FFD700', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Downloading version {targetVersion}...
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'row', visibility: targetVersion ? 'hidden' : 'visible', justifyContent: 'center', p: 2, backgroundColor: '#323232', color: '#FFD700' }}>
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4, width: '500px' }}>
            <Typography variant="h5" gutterBottom>
              Available HMI Versions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {versions.map((v) => (
                <div key={v.version}>
                  <Link color="secondary" id={v.version} href={v.url} target="_blank" rel="noopener noreferrer"> Download HMI v{v.version}</Link>
                </div>
              ))}
            </Box>
          </Box>
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Typography variant="h5" gutterBottom>
              Manuals
            </Typography>
            {manuals.map((m) => (
              <div key={m.title}>
                <Link color="secondary" href={m.url} target="_blank" rel="noopener noreferrer"> {m.title}</Link>
              </div>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export async function getApkVersions() {
  const blobs = await listPublicBlobs('hmi_apk');
  const apkVersions: HMIApkVersion[] = blobs.blobs.map(blob => {
    const versionMatch = blob.name.match(/hmi_(\d+\.\d+\.\d+)\.apk/);
    return versionMatch ? { version: versionMatch[1], url: blob.url } : null;
  })
    .filter((v): v is HMIApkVersion => v !== null);
  return apkVersions;
}
